import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import NotificationBell from "@/components/NotificationBell";
import CommandPalette from "@/components/CommandPalette";
import HurricaneBanner from "@/components/HurricaneBanner";
import PageTransition from "@/components/PageTransition";
import { PowerBuiltMark } from "@/components/PowerBuiltMark";
import GlobalProgress from "@/components/GlobalProgress";
import OfflineStatusPill from "@/components/OfflineStatusPill";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationToaster from "@/components/NotificationToaster";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  LayoutDashboard, FolderKanban, FileText, CreditCard, Wrench,
  Users, MessageSquare, LogOut, Activity, Briefcase, Menu, X, Calendar, BarChart3, Star, FileSignature, CalendarClock,
  Bell, ShieldCheck, Search, Sun, Moon, Settings as SettingsIcon, TrendingUp, Scale, ClipboardList, FileDown, Truck, ShoppingCart, TrendingDown, Mail, Book, BookOpen, Receipt, Landmark, Clock, ChevronDown
} from "lucide-react";

// Grouped admin nav — matches the master spec IA. Users toggle groups
// open/closed; the state persists in localStorage. Overview is always
// expanded on first load.
const ADMIN_GROUPS = [
  { label: "Overview", items: [
    { to: "/admin/today", label: "Today", icon: Sun },
    { to: "/admin/executive", label: "Executive", icon: TrendingUp },
    { to: "/admin/approvals", label: "Approvals", icon: ShieldCheck },
    { to: "/admin/data-integrity", label: "Data Integrity", icon: ShieldCheck },
    { to: "/admin/notifications", label: "Notifications", icon: Bell },
  ]},
  { label: "Customers & Sales", items: [
    { to: "/admin/clients", label: "Clients", icon: Users },
    { to: "/admin/sales-pipeline", label: "Pipeline", icon: TrendingUp },
    { to: "/admin/requests", label: "Requests", icon: FileText },
    { to: "/admin/project-messages", label: "Client Messages", icon: MessageSquare },
    { to: "/admin/estimates", label: "Quotes", icon: FileSignature },
    { to: "/admin/packages", label: "Packages", icon: Sun },
    { to: "/admin/expansions", label: "Expansion Requests", icon: TrendingUp },
  ]},
  { label: "Projects & Operations", items: [
    { to: "/admin/projects", label: "Projects", icon: FolderKanban },
    { to: "/admin/work-orders", label: "Work Orders", icon: Wrench },
    { to: "/admin/dispatch", label: "Dispatch", icon: CalendarClock },
    { to: "/admin/project-stages", label: "Stage Checklists", icon: ClipboardList },
  ]},
  { label: "Team", items: [
    { to: "/admin/employees", label: "Employees", icon: Briefcase },
    { to: "/admin/timesheets", label: "Timesheets", icon: Clock },
    { to: "/admin/leave", label: "Leave", icon: Calendar },
    { to: "/admin/payroll", label: "Payroll", icon: Calendar },
  ]},
  { label: "Inventory & Purchasing", items: [
    { to: "/admin/materials", label: "Inventory", icon: Briefcase },
    { to: "/admin/suppliers", label: "Suppliers", icon: Truck },
    { to: "/admin/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
    { to: "/admin/inventory-forecast", label: "Demand Forecast", icon: TrendingDown },
  ]},
  { label: "Finance", items: [
    { to: "/admin/payments", label: "Invoices", icon: CreditCard },
    { to: "/admin/finances", label: "Payments Received", icon: BarChart3 },
    { to: "/admin/chart-of-accounts", label: "Chart of Accounts", icon: Book },
    { to: "/admin/journal-entries", label: "Journal Entries", icon: BookOpen },
    { to: "/admin/pnl", label: "P&L / Balance", icon: Scale },
    { to: "/admin/bank-recon", label: "Bank Recon", icon: Landmark },
    { to: "/admin/project-profit", label: "Project Profit", icon: TrendingUp },
    { to: "/admin/tot-report", label: "Tax (TOT)", icon: Receipt },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  ]},
  { label: "Marketing", items: [
    { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  ]},
  { label: "Administration", items: [
    { to: "/admin/access-control", label: "Users & Permissions", icon: ShieldCheck },
    { to: "/admin/audit", label: "Audit Log", icon: Activity },
    { to: "/admin/emails", label: "Email Log", icon: Mail },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
  ]},
];

const NAV_BY_ROLE = {
  client_pending: [{ label: "", items: [
    { to: "/dashboard", label: "Welcome", icon: LayoutDashboard },
    { to: "/dashboard/calculator", label: "Savings Calculator", icon: Sun },
    { to: "/dashboard/services", label: "Schedule Consultation", icon: Calendar },
    { to: "/dashboard/profile", label: "Profile", icon: Users },
  ]}],
  client: [{ label: "", items: [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/dashboard/projects", label: "My Projects", icon: FolderKanban },
    { to: "/dashboard/systems", label: "My Systems", icon: Sun },
    { to: "/dashboard/handoff", label: "Handoff Reports", icon: FileDown },
    { to: "/dashboard/packages", label: "Solar Packages", icon: Sun },
    { to: "/dashboard/services", label: "Services", icon: Wrench },
    { to: "/dashboard/estimates", label: "Estimates", icon: FileSignature },
    { to: "/dashboard/invoices", label: "Invoices", icon: FileText },
    { to: "/dashboard/activity", label: "Activity", icon: Activity },
  ]}],
  employee: [{ label: "", items: [
    { to: "/employee", label: "My Day", icon: LayoutDashboard },
    { to: "/employee/work-orders", label: "Work Orders", icon: Briefcase },
    { to: "/employee/daily-log", label: "Daily Log", icon: FileText },
    { to: "/employee/leave", label: "Time Off", icon: Calendar },
    { to: "/employee/notifications", label: "Notifications", icon: Bell },
    { to: "/employee/profile", label: "Profile", icon: Users },
  ]}],
  admin: ADMIN_GROUPS,
  // Super-admin sees exactly the same nav as admin (plus access control is
  // already inside ADMIN_GROUPS.Infrastructure).
  super_admin: ADMIN_GROUPS,
};

function NavGroup({ group, isActive, onNavigate, currentPath }) {
  const label = group.label || "";
  const key = `pb_nav_open_${(label || "default").toLowerCase()}`;
  // Default: Overview open, others collapsed. Auto-open if the current
  // route lives inside this group so the user always sees where they are.
  const containsActive = group.items.some((it) => currentPath.startsWith(it.to));
  const [open, setOpen] = React.useState(() => {
    if (containsActive) return true;
    try {
      const saved = localStorage.getItem(key);
      if (saved !== null) return saved === "1";
    } catch {}
    return label === "Overview";
  });

  React.useEffect(() => {
    if (containsActive && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containsActive]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { localStorage.setItem(key, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  if (!label) {
    // Ungrouped nav (client/employee) — render items directly, no toggler.
    return (
      <div className="space-y-1">
        {group.items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`group relative flex items-center gap-3 px-3 py-2 min-h-[40px] md:min-h-0 rounded-md text-sm transition-all ${
              isActive(item.to)
                ? "bg-[#C9A356] text-[#0A2540] font-semibold shadow-[0_0_0_1px_rgba(201,163,86,0.4),0_4px_12px_-4px_rgba(201,163,86,0.4)]"
                : "text-slate-300 hover:bg-white/5 hover:text-white hover:translate-x-0.5"
            }`}
          >
            <item.icon className={`w-4 h-4 transition-transform ${isActive(item.to) ? "" : "group-hover:scale-110"}`} />
            {item.label}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={toggle}
        data-testid={`nav-section-${label.toLowerCase().replace(/\s+/g, "-")}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2.5 md:py-2 min-h-[44px] md:min-h-[36px] text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 hover:text-slate-300 focus-visible:text-slate-200 focus-visible:outline-none focus-visible:bg-white/5 rounded-md transition"
      >
        <span>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="space-y-1 pb-2">
          {group.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`group relative flex items-center gap-3 px-3 py-2 min-h-[40px] md:min-h-0 rounded-md text-sm transition-all ${
                isActive(item.to)
                  ? "bg-[#C9A356] text-[#0A2540] font-semibold shadow-[0_0_0_1px_rgba(201,163,86,0.4),0_4px_12px_-4px_rgba(201,163,86,0.4)]"
                  : "text-slate-300 hover:bg-white/5 hover:text-white hover:translate-x-0.5"
              }`}
            >
              <item.icon className={`w-4 h-4 transition-transform ${isActive(item.to) ? "" : "group-hover:scale-110"}`} />
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, loading, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center bg-[#F8FAFC] dark:bg-slate-950 dark:text-white">Loading...</div>;
  }

  const navKey = user.role === "client" && user.access_level !== "approved" ? "client_pending" : user.role;
  const groups = NAV_BY_ROLE[navKey] || NAV_BY_ROLE.client;

  const isActive = (to) => {
    if (to === "/dashboard" || to === "/admin" || to === "/employee") {
      return location.pathname === to;
    }
    return location.pathname.startsWith(to);
  };

  const openCmdK = () => {
    // Programmatically dispatch ⌘K so CommandPalette opens
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
  };

  const homeTo =
    user?.role === "admin" || user?.role === "super_admin" ? "/admin"
    : user?.role === "employee" ? "/employee"
    : "/dashboard";

  return (
    <div className="min-h-screen flex bg-[#F8FAFC] dark:bg-slate-950">
      <HurricaneBanner compact />

      {/* Sidebar — sticky + viewport-height so its inner nav creates its own
          scroll context (previously the sidebar grew with page content and
          users had to scroll the whole window to reach bottom items). */}
      <aside className={`${mobileOpen ? 'fixed inset-y-0 left-0 z-50 overflow-y-auto' : 'hidden'} md:flex md:sticky md:top-0 md:h-screen md:flex-col w-72 bg-[#0A2540] dark:bg-slate-900 dark:border-r dark:border-slate-800 text-white shrink-0`}>
        <div className="flex items-center justify-between p-5">
          <Link to={homeTo} className="flex items-center gap-2.5 group" data-testid="sidebar-logo">
            <PowerBuiltMark className="w-10 h-10 transition-transform group-hover:rotate-3" />
            <div className="leading-tight">
              <div className="font-heading font-bold tracking-wider text-sm">POWERBUILT</div>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={() => setMobileOpen(false)} className="md:hidden text-white w-11 h-11 grid place-items-center rounded-md hover:bg-white/5" data-testid="close-sidebar" aria-label="Close menu">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 pb-4 border-b border-white/10">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{user.role === "super_admin" ? "admin" : user.role}</div>
          <div className="font-medium text-sm" data-testid="sidebar-user-name">{user.name}</div>
          {user.customer_code && (
            <div className="text-xs text-[#C9A356] mt-1 font-mono" data-testid="sidebar-customer-code">{user.customer_code}</div>
          )}
          {user.role === "client" && user.access_level !== "approved" && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-500/15 border border-amber-500/30 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300" data-testid="sidebar-pending-badge">Awaiting approval</span>
            </div>
          )}
        </div>

        {/* ⌘K trigger (admin + super_admin) */}
        {(user.role === "admin" || user.role === "super_admin") && (
          <div className="px-3 pt-3">
            <button
              onClick={openCmdK}
              data-testid="cmdk-trigger"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 text-sm transition"
            >
              <Search className="w-4 h-4" />
              <span>Search…</span>
              <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/10 border border-white/10">⌘K</kbd>
            </button>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1.5">
          {groups.map((g) => (
            <NavGroup
              key={g.label || "default"}
              group={g}
              isActive={isActive}
              onNavigate={() => setMobileOpen(false)}
              currentPath={location.pathname}
            />
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          {/* Language switcher */}
          <LanguageSwitcher compact />
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white w-full"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="sidebar-logout"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white w-full"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="md:hidden flex items-center justify-between p-4 border-b border-[#E2E8F0] dark:border-slate-800 bg-white dark:bg-slate-900">
          <button onClick={() => setMobileOpen(true)} data-testid="open-sidebar" className="text-[#0A2540] dark:text-white w-11 h-11 grid place-items-center rounded-md" aria-label="Open menu">
            <Menu className="w-6 h-6" />
          </button>
          <Link to={homeTo} data-testid="mobile-logo">
            <PowerBuiltMark className="w-9 h-9" darkOnLight />
          </Link>
          <div className="flex items-center gap-1">
            <OfflineStatusPill />
            <button onClick={toggleTheme} className="text-[#0A2540] dark:text-white w-11 h-11 grid place-items-center rounded-md" data-testid="theme-toggle-mobile" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {/* Desktop floating status pill */}
        <div className="hidden md:flex justify-end px-10 pt-4">
          <OfflineStatusPill />
        </div>
        <div className="p-6 md:p-10">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>
      </main>

      {/* Cmd-K palette mounts globally for admins */}
      {(user.role === "admin" || user.role === "super_admin") && <CommandPalette />}
      <GlobalProgress />
      <InstallPrompt />
      <NotificationToaster />
    </div>
  );
}
